"use client";

import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { GondolaLoading } from "@/components/ui/gondolaLoading";
import { UserStatus } from "@/generated/email_parser.openapi";
import { defaultInitialPageRoute } from "@/lib/utils";
import { CookieName } from "@/types/nextCookie";
import { useSession } from "next-auth/react";
import { useRouter } from "next-nprogress-bar";
import React, { PropsWithChildren, useEffect } from "react";

export const excludedRoutes = [
  "/",
  "/points",
  "/personalized-search",
  "/rate-monitoring",
  "/earn",
];

// Executive Assistant invite/response emails reuse `section=settings` for their
// Accept / Manage Access buttons, so those links are not the opt-out link.
const nonOptOutSettingsEmailIds = new Set(["eai", "ear"]);

const PageProtectedAccess: React.FC<PropsWithChildren> = ({ children }) => {
  const [loading, setLoading] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(false);
  const session = useSession();
  const router = useRouter();
  const { user } = useGlobalStore();

  useEffect(() => {
    // Account Information path, including the legacy `/profile?section=...` forms
    // still emitted by emails. `section=settings` is the "let us know" opt-out link
    // in most marketing emails; waitlist recipients can't sign in, so it must reach
    // the public unsubscribe page rather than a sign-in wall.
    const params = new URLSearchParams(window.location.search);
    const section = params.get("section");
    const isOptOutSettingsLink =
      section === "settings" &&
      !nonOptOutSettingsEmailIds.has(params.get("e") ?? "");
    const isAccountInformationPath =
      window.location.pathname === "/profile/account-information" ||
      (window.location.pathname === "/profile" &&
        (section === "account-information" || isOptOutSettingsLink));

    if (session.status === "loading") return;
    if (session.status === "unauthenticated") {
      // Redirect to the unsubscribe page if the user is not authenticated with profile settings
      if (isAccountInformationPath) {
        router.replace("/unsubscribe");
        return;
      }
      // Exception for the landing page, when the user is not logged in
      if (excludedRoutes.includes(window.location.pathname)) {
        setHasAccess(true);
        setLoading(false);
      } else {
        // Redirect to signin page with current path as callback
        const callbackUrl = window.location.pathname + window.location.search;
        router.replace(
          `/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`
        );
      }
      return;
    }

    // Check if the user has access to the page based on the user status
    if (session.status === "authenticated" && user?.status) {
      // If the email sync token has expired, redirect to /update-account to reconnect.
      const syncTokenExpired = document.cookie
        .split(";")
        .some((c) =>
          c.trim().startsWith(`${CookieName.SYNC_TOKEN_EXPIRED}=true`)
        );
      if (syncTokenExpired && window.location.pathname !== "/update-account") {
        document.cookie = `${CookieName.SYNC_TOKEN_EXPIRED}=; path=/; max-age=0`;
        router.replace("/update-account");
        return;
      }
      if (user.status === UserStatus.SiteAccess && isAccountInformationPath) {
        router.replace("/unsubscribe");
      } else if (
        user.status === UserStatus.EmailSynced ||
        user.status === UserStatus.SiteAccessFull
      ) {
        if (window.location.pathname === "/") {
          router.replace(defaultInitialPageRoute);
        } else {
          setHasAccess(true);
          setLoading(false);
        }
      } else if (window.location.pathname !== "/onboarding") {
        router.replace("/onboarding");
      }
      return;
    } else {
      router.replace("/");
      return;
    }
  }, [router, session, user]);

  // Show loading spinner while checking user status
  if (loading) {
    return (
      <GondolaLoading containerClassName="h-screen place-content-center" />
    );
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return null;
};

export default PageProtectedAccess;
